import { ControllerInput, FlightController, PickContext, ScreenSpaceConversions } from "@novorender/api";

import { clamp } from "utils/math";
import { memoizeByObject } from "utils/misc";

export const EMPTY_SPACE_ZOOM_SPEED = 3;
export const ZOOM_DAMPING_FACTOR = 0.15;
const DEFAULT_PROPORTIONAL_SPEED_MIN = 1;
const DEFAULT_PROPORTIONAL_SPEED_MAX = 300;
const FRAME_DURATION_MS = 1000 / 60;
const MIN_ZOOM_SPEED = 1e-6;

type Modifiers = ReturnType<FlightController["modifiers"]>;
type ProportionalCameraSpeed = {
    min: number;
    max: number;
};

type ZoomPickState = {
    generation: number;
    lastPickMissed?: boolean;
};

export function lerpLogSpace(current: number, target: number, t: number): number {
    const from = Math.max(current, MIN_ZOOM_SPEED);
    const to = Math.max(target, MIN_ZOOM_SPEED);
    return Math.exp(Math.log(from) + (Math.log(to) - Math.log(from)) * clamp(t, 0, 1));
}

function observeZoomPicks(pick: PickContext, state: ZoomPickState): PickContext {
    return {
        ...pick,
        pick: async (...args: Parameters<PickContext["pick"]>) => {
            const options = args[2];
            const observesResult =
                options?.async === true &&
                options.sampleDiscRadius === 200 &&
                options.searchType === "closestToPickCenter";
            const generation = observesResult ? ++state.generation : state.generation;
            const result = await pick.pick(...args);
            if (observesResult && generation === state.generation) {
                state.lastPickMissed = result === undefined;
            }
            return result;
        },
    };
}

/**
 * Extends FlightController with a `rotationalInversion` flag that controls
 * whether left mouse button drag maps to camera rotation in the standard direction.
 *
 * When `rotationalInversion = true` (default): only the left-mouse-button axes
 * are negated before passing to the base controller — producing a "turntable /
 * look-around" feel matching the legacy models tool. Right mouse button orbit
 * and keyboard rotation are unaffected.
 *
 * Single-finger touch is left unsigned. The SDK FlightController already signs
 * touch opposite mouse, so negating `touch_1_move_*` here would cancel that
 * convention and invert mobile drag (BM-12542). Do not re-add those negations
 * for symmetry with the mouse axes.
 *
 * When `rotationalInversion = false`: camera behaves as standard.
 *
 * Roll is left to the base controller (horizon lock). SpaceMouse Object-mode past
 * vertical is handled by SpaceMouseFlightController while a session is live.
 */
const defineInvertedFlightController = memoizeByObject(
    (Base: typeof FlightController) =>
        class InvertedFlightController extends Base {
            rotationalInversion: boolean = true;
            private readonly zoomPickState: ZoomPickState;
            private proportionalCameraSpeed: ProportionalCameraSpeed | undefined;
            private smoothedWheelModifier = EMPTY_SPACE_ZOOM_SPEED;
            private lastZoomSmoothingTime = performance.now();

            constructor(input: ControllerInput, pick: PickContext, conversions: ScreenSpaceConversions) {
                const zoomPickState: ZoomPickState = { generation: 0 };
                super(input, observeZoomPicks(pick, zoomPickState), conversions);
                this.zoomPickState = zoomPickState;
            }

            override updateParams(params: Parameters<FlightController["updateParams"]>[0]): void {
                if ("proportionalCameraSpeed" in params) {
                    const next = this.resolveProportionalCameraSpeed(params.proportionalCameraSpeed);
                    const enabledChanged = Boolean(this.proportionalCameraSpeed) !== Boolean(next);
                    this.proportionalCameraSpeed = next;
                    if (enabledChanged) {
                        this.smoothedWheelModifier = this.emptySpaceZoomSpeed();
                        this.lastZoomSmoothingTime = performance.now();
                    }
                }
                super.updateParams(params);
            }

            protected override modifiers(): Modifiers {
                const modifiers = super.modifiers();
                if (!this.proportionalCameraSpeed) {
                    return modifiers;
                }

                const target =
                    this.zoomPickState.lastPickMissed === true
                        ? this.emptySpaceZoomSpeed()
                        : modifiers.mouseWheelModifier;
                const now = performance.now();
                const elapsed = Math.max(now - this.lastZoomSmoothingTime, 0);
                const damping = 1 - Math.pow(1 - ZOOM_DAMPING_FACTOR, elapsed / FRAME_DURATION_MS);

                this.smoothedWheelModifier = lerpLogSpace(this.smoothedWheelModifier, target, damping);
                this.lastZoomSmoothingTime = now;

                return { ...modifiers, mouseWheelModifier: this.smoothedWheelModifier };
            }

            protected override getTransformations() {
                if (!this.rotationalInversion) {
                    return super.getTransformations();
                }

                const axes = this.input.axes;
                axes.mouse_lmb_move_x = -axes.mouse_lmb_move_x;
                axes.mouse_lmb_move_y = -axes.mouse_lmb_move_y;

                return super.getTransformations();
            }

            private resolveProportionalCameraSpeed(
                proportionalCameraSpeed: Parameters<FlightController["updateParams"]>[0]["proportionalCameraSpeed"],
            ): ProportionalCameraSpeed | undefined {
                if (!proportionalCameraSpeed) {
                    return undefined;
                }
                return {
                    min:
                        proportionalCameraSpeed.min ??
                        this.proportionalCameraSpeed?.min ??
                        DEFAULT_PROPORTIONAL_SPEED_MIN,
                    max:
                        proportionalCameraSpeed.max ??
                        this.proportionalCameraSpeed?.max ??
                        DEFAULT_PROPORTIONAL_SPEED_MAX,
                };
            }

            private emptySpaceZoomSpeed(): number {
                if (!this.proportionalCameraSpeed) {
                    return EMPTY_SPACE_ZOOM_SPEED;
                }
                return clamp(
                    EMPTY_SPACE_ZOOM_SPEED,
                    this.proportionalCameraSpeed.min,
                    this.proportionalCameraSpeed.max,
                );
            }
        },
);

/** The class built on the current `FlightController` binding, so call it after `initEngine()`. */
export function invertedFlightControllerClass() {
    return defineInvertedFlightController(FlightController);
}

export type InvertedFlightController = InstanceType<ReturnType<typeof invertedFlightControllerClass>>;
